import React from 'react';

// the store module only: the flow barrel also carries the flow's dialogs.
import { useFlowStore } from '@/features/flow/store';
import type { SpeedGasMetricsActive } from '@/lib/api/speed';
import { cn } from '@/lib/utils';

import type { ChainLoadLevel } from '../hooks/useChainLoad';
import { useChainLoad } from '../hooks/useChainLoad';

interface ChainLoadIndicatorProps {
    /** Extra classes for placement (e.g. `self-end` in the mobile control row). */
    className?: string;
}

const SEGMENT_COUNT = 5;
/** Base pixel height of the shortest signal-bar segment; each step adds 1px. */
const SEGMENT_BASE_HEIGHT = 4;

/** Level → semantic CSS variable. Applied inline so no hex is hardcoded. */
const LEVEL_COLOR: Record<ChainLoadLevel, string> = {
    low: 'var(--success)',
    mid: 'var(--info)',
    high: 'var(--destructive)',
};

function formatPct(value: number | null): string | null {
    return value === null ? null : `${Math.round(value * 100)}%`;
}

/**
 * Builds the native-title breakdown, e.g.
 * `HyperEVM load — now 62% · 1m 43% · 5m 40%. High load can delay trade
 * confirmation.` Warm-up windows that are still `null` are omitted.
 */
function buildTitle(metrics: SpeedGasMetricsActive): string {
    const parts: string[] = [`now ${formatPct(metrics.current)}`];
    const windows: Array<[string, number | null]> = [
        ['1m', metrics.avg1m],
        ['5m', metrics.avg5m],
        ['15m', metrics.avg15m],
        ['30m', metrics.avg30m],
    ];
    for (const [label, value] of windows) {
        const pct = formatPct(value);
        if (pct !== null) parts.push(`${label} ${pct}`);
    }
    return `HyperEVM load: ${parts.join(
        ' · ',
    )}. High load can delay trade confirmation.`;
}

/**
 * Compact HUD telemetry for HyperEVM congestion: a `NET.LOAD` label, a
 * 5-segment signal-strength bar, and a mono percent. Colored success/info/
 * destructive by load band. Renders nothing unless the backend feature is live —
 * a disabled flag, a 404, or a query error all hide it entirely.
 */
const ChainLoadMeter: React.FC<ChainLoadIndicatorProps> = ({ className }) => {
    const { active, level, displayLoad, metrics } = useChainLoad();

    // Render nothing unless the feature is on and parsed. The extra guards keep
    // the type narrowed so `metrics` is the enabled payload below.
    if (!active || displayLoad === null || metrics?.enabled !== true) {
        return null;
    }

    const color = LEVEL_COLOR[level];
    const filled = Math.min(
        SEGMENT_COUNT,
        Math.max(0, Math.round(displayLoad * SEGMENT_COUNT)),
    );

    return (
        <div
            title={buildTitle(metrics)}
            className={cn('flex items-center gap-1.5 select-none', className)}
        >
            {level === 'high' && (
                <span
                    aria-hidden="true"
                    className="hud-blink h-1 w-1 rounded-full"
                    style={{ backgroundColor: color }}
                />
            )}
            <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                NET.LOAD
            </span>
            <span className="flex items-end gap-px" aria-hidden="true">
                {Array.from({ length: SEGMENT_COUNT }).map((_, index) => (
                    <span
                        key={index}
                        className={cn(
                            'w-[3px]',
                            index >= filled && 'bg-muted-foreground/20',
                        )}
                        style={{
                            height: `${SEGMENT_BASE_HEIGHT + index}px`,
                            ...(index < filled
                                ? { backgroundColor: color }
                                : {}),
                        }}
                    />
                ))}
            </span>
            <span
                className="font-mono text-[11px] tabular-nums"
                style={{ color }}
            >
                {formatPct(displayLoad)}
            </span>
        </div>
    );
};

ChainLoadMeter.displayName = 'ChainLoadMeter';

/**
 * V1 only: HyperEVM is where the custodial flow's service wallets confirm trades. The browser wallet flow (V2) signs
 * straight to Hyperliquid, so its load says nothing about a V2 trade (and the metrics are not even fetched there).
 */
const ChainLoadIndicator: React.FC<ChainLoadIndicatorProps> = (props) => {
    const isBrowserWalletFlow = useFlowStore((s) => s.active === 'v2');
    return isBrowserWalletFlow ? null : <ChainLoadMeter {...props} />;
};

ChainLoadIndicator.displayName = 'ChainLoadIndicator';

export default ChainLoadIndicator;
